import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";

MerchantAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  routeId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const BUSINESS_LINES = ["", "RETAIL", "COMMERCE", "SMART_TRAFFIC"];

export default function MerchantAssignmentDialog({ open, routeId, onClose, onSave }) {
  const api = useApi();
  const [scope, setScope] = useState("individual");
  const [merchants, setMerchants] = useState([]);   // GatewayMerchantSummary list
  const [organizations, setOrganizations] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { businessLine: "", isDefault: true, organizationId: "", category: "" },
  });

  useEffect(() => {
    if (!open) return;
    reset({ businessLine: "", isDefault: true, organizationId: "", category: "" });
    setScope("individual");
    setSelectedMerchants([]);

    // Use the new endpoint that returns gateway UUID + MID + name directly
    api.get("settlement-routes/merchants")
      .then((res) => setMerchants(res.data || []))
      .catch(() => toast.error("Failed to load merchants"));

    api.get("admin/browseMerchantGroup")
      .then((res) => setOrganizations(res.data?.body || res.data || []))
      .catch(() => {});
  }, [open]);

  const onSubmit = (data) => {
    const payload = {
      routeId,
      businessLine: data.businessLine || null,
      isDefault: data.isDefault,
    };
    if (scope === "individual") {
      // m.id is the gateway UUID from GatewayMerchantSummary — use directly as merchantIds
      payload.merchantIds = selectedMerchants.map((m) => m.id);
    } else if (scope === "organization") {
      payload.organizationId = data.organizationId;
    } else if (scope === "category") {
      payload.category = data.category;
    }
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Merchants to Route</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" fontWeight="medium">Assignment Scope</MDTypography>
              <RadioGroup row value={scope} onChange={(e) => setScope(e.target.value)} sx={{ mt: 0.5 }}>
                <FormControlLabel value="individual" control={<Radio />} label="Individual Merchants" />
                <FormControlLabel value="organization" control={<Radio />} label="By Organization" />
                <FormControlLabel value="category" control={<Radio />} label="By Category" />
              </RadioGroup>
            </Grid>

            {scope === "individual" && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={merchants}
                  getOptionLabel={(m) => `${m.name || ""} (${m.mid || ""})`}
                  value={selectedMerchants}
                  onChange={(_, v) => setSelectedMerchants(v)}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Merchants" variant="outlined" size="small" />
                  )}
                />
                {selectedMerchants.length > 0 && (
                  <MDTypography variant="caption" color="info" mt={0.5} display="block">
                    {selectedMerchants.length} merchant(s) selected
                  </MDTypography>
                )}
              </Grid>
            )}

            {scope === "organization" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="org-label">Organization</InputLabel>
                  <Controller
                    name="organizationId"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} labelId="org-label" label="Organization">
                        <MenuItem value="">- Select Organization -</MenuItem>
                        {organizations.map((o) => (
                          <MenuItem key={o.groupMid || o.id} value={o.groupMid || o.id}>
                            {o.companyName || o.referenceCompanyName || o.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            {scope === "category" && (
              <Grid item xs={12}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <MDInput {...field} label="Merchant Category (typeEstablishment)" fullWidth />
                  )}
                />
              </Grid>
            )}

            {/* <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="biz-line-label">Business Line</InputLabel>
                <Controller
                  name="businessLine"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} labelId="biz-line-label" label="Business Line">
                      {BUSINESS_LINES.map((b) => (
                        <MenuItem key={b} value={b}>{b || "None (applies to all)"}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={6} display="flex" alignItems="center">
              <Controller
                name="isDefault"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Set as Default Assignment"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
          <MDButton type="submit" variant="gradient" color="info">Assign</MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
